import { useState, useEffect } from "react";
import { Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import DashboardStats from "../../../common/Dashboard/DashboardStats";
import RefreshButtonTable from "../../../common/RefreshButtonTable";
import { useGetTeacherReport } from "../../../../hooks/school/teacher/useGetTeacherReport";
import ReportSkeleton from "./ReportSkeleton";
import TableError from "../../common/TableError";
import TablePagination from "../../TeacherAdmin/pagination/TablePagination";
import { ITEMS_PER_PAGE } from "../../../../constant/constant";
import "../../../../common/styles/table.css";

/**
 * @typedef {import("../../../../hooks/school/teacher/useGetTeacherReport").TeacherReportItem} TeacherReportItem
 */

/**
 * TeacherReport - Displays teacher report list with stats and table
 */
const TeacherReport = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);

  const { loading, error, reportData, getTeacherReport, metaData } =
    useGetTeacherReport();

  // Fetch teacher report on mount and page change
  useEffect(() => {
    getTeacherReport(currentPage, ITEMS_PER_PAGE);
  }, [currentPage]);

  const handleRefresh = () => {
    getTeacherReport(currentPage, ITEMS_PER_PAGE);
  };

  /**
   * Generate teacher stats from API data
   */
  const getTeacherStats = () => {
    if (!reportData) {
      return [
        {
          label: "Total Teachers",
          value: "-",
          icon: "fa-graduation-cap",
          colorClass: "dashboard-stat-card-purple",
        },
        {
          label: "Avg. Student Progress",
          value: "-",
          icon: "fa-user",
          colorClass: "dashboard-stat-card-green",
        },
        {
          label: "Highest Performing Teacher",
          value: "-",
          icon: "fa-file-arrow-down",
          colorClass: "dashboard-stat-card-peach",
        },
        {
          label: "Lowest Performing Teacher",
          value: "-",
          icon: "fa-file-arrow-down",
          colorClass: "dashboard-stat-card-pastel-blue",
        },
      ];
    }

    return [
      {
        label: "Total Teachers",
        value: metaData.total || 0,
        icon: "fa-graduation-cap",
        colorClass: "dashboard-stat-card-purple",
      },
      {
        label: "Avg. Student Progress",
        value: `${reportData.avg_student_progress || 0}%`,
        icon: "fa-user",
        colorClass: "dashboard-stat-card-green",
      },
      {
        label: "Highest Performing Teacher",
        value: reportData.highest_performing?.name || "-",
        icon: "fa-file-arrow-down",
        colorClass: "dashboard-stat-card-peach",
      },
      {
        label: "Lowest Performing Teacher",
        value: reportData.lowest_performing?.name || "-",
        icon: "fa-file-arrow-down",
        colorClass: "dashboard-stat-card-pastel-blue",
      },
    ];
  };

  /** @type {TeacherReportItem[]} */
  const teacherList = reportData?.teacher_list?.data || [];
  const totalPages = metaData?.last_page || 1;

  /**
   * Handle teacher row click - navigate to detail page
   */
  const handleTeacherClick = (teacher) => {
    navigate(`/school-admin/school/report/teachers/${teacher.id}`);
  };

  // Show skeleton on initial load
  if (loading && reportData === undefined) {
    return (
      <>
        <div className="pt-2">
          <RefreshButtonTable handleRefresh={handleRefresh} loading={loading} />
        </div>
        <ReportSkeleton />
      </>
    );
  }

  return (
    <>
      <div className="pt-2">
        <RefreshButtonTable handleRefresh={handleRefresh} loading={loading} />
      </div>

      <DashboardStats stats={getTeacherStats()} />

      {/* Show error state */}
      {error ? (
        <TableError
          error={error}
          handleRefresh={handleRefresh}
          loading={loading}
        />
      ) : teacherList.length === 0 ? (
        <div className="alert alert-info mt-3" role="alert">
          No teachers found.
        </div>
      ) : (
        <div className="custom-table-container mt-3">
          <div className="custom-table-wrapper">
            <Table hover className="custom-table">
              <thead className="custom-table-head">
                <tr>
                  <th>Name</th>
                  <th>Classes</th>
                  <th>Students</th>
                  <th>Avg. Progress</th>
                  <th>Pass Rate</th>
                  <th>Lessons Assigned</th>
                </tr>
              </thead>
              <tbody>
                {teacherList.map((teacher) => (
                  <tr
                    key={teacher.id}
                    onClick={() => handleTeacherClick(teacher)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{teacher.name || "-"}</td>
                    <td>{teacher.class_count || 0}</td>
                    <td>{teacher.student_count || 0}</td>
                    <td>{teacher.avg_progress || 0}%</td>
                    <td>{teacher.pass_rate || 0}%</td>
                    <td>{teacher.lessons_assigned || 0}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <TablePagination
            totalPage={totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            disabled={loading}
          />
        </div>
      )}
    </>
  );
};

export default TeacherReport;
